import { useState } from 'react';
import { Rich, useT } from '../lib/i18n';
import { Stepper } from './Stepper';

/**
 * How long, asked before when.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS THE FIRST CONTROL ON THE PAGE
 * ---------------------------------------------------------------------------
 *
 * Every booking site asks for dates first because every booking site is copying
 * the last one. It is wrong here, and §3.1 says exactly why: a stay must be
 * housed in ONE unit for all of its nights, so whether a Tuesday can be arrived
 * on depends on how long the guest is staying. The same date is free for three
 * nights and full for ten.
 *
 * A calendar that shades dates before it knows the length therefore has to
 * answer some other question — usually "is anything free that night", which is
 * the count-per-night test §3.1 identifies as the one that sells bookings
 * nobody can house. The fix is not a cleverer calendar. It is asking in the
 * order the answer depends on.
 *
 * So: the length picker is step one, it is not skippable, and the calendar next
 * to it stays visibly locked rather than disappearing — a guest who cannot see
 * the calendar cannot tell whether it is broken or waiting for them. The reason
 * is stated in one sentence on screen, because a guest who understands why is a
 * guest who does not fight it.
 *
 * ---------------------------------------------------------------------------
 * WHY CHIPS AND A STEPPER RATHER THAN A NUMBER FIELD
 * ---------------------------------------------------------------------------
 *
 * A weekend, a long weekend, a week, a fortnight: four taps cover most of what
 * a park sells, and each one is a phrase a guest already thinks in. The stepper
 * behind "another length" exists for the rest without making everybody meet a
 * spinner first.
 */

/** The lengths a park actually sells, most of the time. */
const COMMON = [2, 3, 4, 7, 14];

const MIN = 1;
const MAX = 60;

export function LengthPicker({ nights, onChange, headingId }: {
  nights: number | null;
  onChange: (nights: number) => void;
  headingId?: string;
}) {
  const t = useT();
  const [custom, setCustom] = useState(() => nights !== null && !COMMON.includes(nights));

  const showStepper = custom || (nights !== null && !COMMON.includes(nights));

  return (
    <div className="pw-stack-tight">
      <div className="pw-chips" role="group" aria-labelledby={headingId}>
        {COMMON.map((n) => (
          <button
            key={n}
            type="button"
            className="pw-chip"
            aria-pressed={nights === n}
            onClick={() => { setCustom(false); onChange(n); }}
          >
            {t('search.nights', { count: n })}
          </button>
        ))}
        <button
          type="button"
          className="pw-chip"
          aria-pressed={showStepper}
          onClick={() => {
            setCustom(true);
            // Seed the stepper with something sensible rather than leaving the
            // calendar locked while the guest hunts for a starting number.
            if (nights === null || COMMON.includes(nights)) onChange(5);
          }}
        >
          {t('search.customLength')}
        </button>
      </div>

      {showStepper && (
        <div className="pw-row">
          <span className="pw-label" id="pw-length-label">{t('search.nightsLabel')}</span>
          <Stepper
            value={nights ?? MIN}
            min={MIN}
            max={MAX}
            onChange={onChange}
            name={t('search.nightsLabel')}
            describedBy="pw-length-label"
          />
        </div>
      )}
    </div>
  );
}
